import {
  IconChevronRight,
  IconHeart,
  IconRepeat,
  IconUser
} from '@tabler/icons-react'
import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'
import { useSession } from 'next-auth/react'

import CategoriesNavigationPromotions from 'composition/navigation/CategoriesNavigationPromotions'
import DeliveryReservationModal from 'composition/navigation/DeliveryReservationModal'
import ProfileDropdown from 'composition/navigation/ProfileDropdown'
import { useNavigation } from 'composition/navigation/useNavigation'
import { isAlgoliaIntegrationConfigured } from 'lib/algolia'
import { CartDrawer, DeliverySlotTimer, useDisplayCart } from 'modules/cart'
import { CategoriesNavigationWeb } from 'modules/catalog'
import { useRibbon } from 'modules/content'
import {
  CustomerTypes,
  extractCustomerFromClientSession
} from 'modules/customer'
import { GuestDeliveryAreaCheck, useDeliverySupport } from 'modules/delivery'
import {
  FetchUpsellingResponse,
  NavMenuCategory,
  ProductTypes
} from 'modules/products'
import { cartAdapters } from 'modules/saleOrders'
import { Search } from 'modules/search'
import { IS_TMS_DELIVERY } from 'shared/config'
import LinkButton, { LinkButtonProps } from 'shared/ui/Button/LinkButton'
import { PATHS } from 'shared/utils/paths'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import styles from './NavigationWeb.module.scss'

interface NavigationWebProps {
  promotedProducts: ProductTypes[]
  categories: NavMenuCategory[]
  menuPromotionItems: FetchUpsellingResponse['data'] | null
}

const NavigationWeb = ({
  categories,
  menuPromotionItems
}: NavigationWebProps) => {
  const session = useSession()
  const user: CustomerTypes | undefined = extractCustomerFromClientSession(session)
  const isLoggedIn = isNotNilOrEmpty(user)
  const cart = useDisplayCart()
  const { isPostalCodeAndCitySupported } = useDeliverySupport()

  const { openablesState, handleOpenablesStateToggle } = useNavigation()
  const { ribbon } = useRibbon()

  return (
    <>
      <nav className={styles.wrapper} {...addTestId('navigation-desktop')}>
        <div className={clsx(styles.barWrapper, styles.mainBarWrapper)}>
          <div className={styles.content}>
            <div className={styles.leftSideWrapper}>
              <Link
                className={styles.logoLink}
                href={PATHS.home}
                {...addTestId('navigation-logo')}
              >
                <Image
                  className={styles.logo}
                  priority
                  src='/static/lokalny-rolnik.svg'
                  alt='Logo Lokalny Rolnik'
                  width={139}
                  height={43}
                />
              </Link>
              {isAlgoliaIntegrationConfigured && (
                <Search
                  open={openablesState.searchDropdown}
                  setOpen={handleOpenablesStateToggle}
                  isRibbonActive={!!ribbon}
                />
              )}
            </div>
            {isLoggedIn ? (
              <div className={styles.loggedInUserContent}>
                {!IS_TMS_DELIVERY ? (
                  <DeliverySlotTimer />
                ) : (
                  <DeliveryReservationModal
                    open={openablesState.deliveryReservation}
                    setOpen={handleOpenablesStateToggle}
                  />
                )}
                <div className={styles.actions}>
                  <ProfileDropdown />
                </div>
                {isNotNilOrEmpty(cart) && (
                  <CartDrawer
                    ports={cartAdapters}
                    open={openablesState.cart}
                    isRibbonActive={!!ribbon}
                    setOpen={handleOpenablesStateToggle}
                  />
                )}
              </div>
            ) : (
              <div className={styles.actions}>
                <GuestDeliveryAreaCheck
                  savePostalCode
                  open={openablesState.deliveryReservationNoUser}
                  setOpen={handleOpenablesStateToggle}
                />
                <div className={styles.navLinks}>
                  <LinkButton
                    href={PATHS.login}
                    size='large'
                    variant='neutralOutline'
                    aria-label='Logowanie'
                    {...addTestId('login-button')}
                  >
                    <IconUser width={24} height={24} />
                  </LinkButton>
                  <LinkButton
                    href={PATHS.register}
                    size='large'
                    variant='primary'
                    {...addTestId('register-button')}
                  >
                    Zarejestruj się
                  </LinkButton>
                </div>
                {isNotNilOrEmpty(cart) && isPostalCodeAndCitySupported && (
                  <CartDrawer
                    ports={cartAdapters}
                    open={openablesState.cart}
                    isRibbonActive={!!ribbon}
                    setOpen={handleOpenablesStateToggle}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <div className={styles.barWrapper}>
          <div className={styles.bottomBar}>
            <div className={styles.bottomLeft}>
              <CategoriesNavigationWeb
                categories={categories}
                promotionsSlot={
                  <CategoriesNavigationPromotions
                    menuPromotionItems={menuPromotionItems?.data || []}
                  />
                }
              />
              <div className={styles.bottomLeftSecondary}>
                <NavigationButton
                  variant='neutralText'
                  size='large'
                  href={PATHS.bestsellers}
                >
                  Specjały
                </NavigationButton>

                <NavigationButton
                  variant='neutralText'
                  size='large'
                  href={PATHS.newestProducts}
                >
                  Nowości
                </NavigationButton>

                {isLoggedIn && (
                  <>
                    <NavigationButton
                      startIcon={<IconHeart size={16} />}
                      variant='neutralText'
                      size='large'
                      href={PATHS.profileFavourites}
                      rel='nofollow'
                    >
                      Ulubione
                    </NavigationButton>

                    <NavigationButton
                      startIcon={<IconRepeat size={16} />}
                      variant='neutralText'
                      size='large'
                      href={PATHS.mostFrequentlyBought}
                      rel='nofollow'
                    >
                      Najczęściej kupowane
                    </NavigationButton>
                  </>
                )}
              </div>
            </div>
            <div className={styles.bottomRight}>
              <NavigationButton
                variant='neutralText'
                size='large'
                href={PATHS.deliveryArea}
                rel='nofollow'
                endIcon={<IconChevronRight className={styles.rightIcon} />}
              >
                O dostawie
              </NavigationButton>
              {isLoggedIn ? (
                <NavigationButton
                  variant='neutralText'
                  size='large'
                  href={PATHS.howToMakeOrder}
                  rel='nofollow'
                  endIcon={<IconChevronRight className={styles.rightIcon} />}
                >
                  Jak złożyć zamówienie?
                </NavigationButton>
              ) : (
                <NavigationButton
                  variant='neutralText'
                  size='large'
                  href={PATHS.howToRegister}
                  rel='nofollow'
                  endIcon={<IconChevronRight className={styles.rightIcon} />}
                >
                  Jak założyć konto?
                </NavigationButton>
              )}
            </div>
          </div>
        </div>
      </nav>
    </>
  )
}

export default NavigationWeb

const NavigationButton = ({ className, ...props }: LinkButtonProps) => (
  <LinkButton {...props} className={clsx(styles.navigationButton, className)} />
)
